import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Switch,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';

import { Controller, useWatch } from 'react-hook-form';

import { CustomAlert, DropdownInput, InfoCircleOutline } from '@aslanpay/portal-common-lib';

import { PayCalendarGpeFieldsProps, PayCalendarGpeSide } from './types';

const GPE_SIDES: PayCalendarGpeSide[] = [
  {
    label: 'Salaried',
    enabledName: 'salariedGpeEnabled',
    capName: 'salariedGpeDaysBeforePaydayCap',
  },
  {
    label: 'Non-salaried',
    enabledName: 'nonSalariedGpeEnabled',
    capName: 'nonSalariedGpeDaysBeforePaydayCap',
  },
];

function PayCalendarGpeFields({
  control,
  errors,
  salariedMatIsPayday,
  nonSalariedMatIsPayday,
  maxDaysBeforePayday,
  serverError,
}: PayCalendarGpeFieldsProps) {
  const matIsPaydayBySide: Record<PayCalendarGpeSide['enabledName'], boolean> = {
    salariedGpeEnabled: salariedMatIsPayday,
    nonSalariedGpeEnabled: nonSalariedMatIsPayday,
  };

  // When cutoff sits the day before payday there are no days between cutoff and payday, so no valid cap
  // exists and Get Paid Early cannot be offered for this calendar.
  const hasValidGpeWindow = maxDaysBeforePayday >= 1;

  // Valid caps run 1..(payday - cutoff - 1): a request can complete on any day after the cutoff (the
  // cutoff day itself is excluded to avoid timing issues) up to and including payday, so the earliest
  // allowed day is cutoff + 1, i.e. (payday - cutoff - 1) days before payday.
  const capOptions = Array.from({ length: Math.max(maxDaysBeforePayday, 0) }, (_, index) => {
    const days = index + 1;
    return { value: days, text: `${days} ${days === 1 ? 'day' : 'days'}` };
  });

  const gpeTooltip = hasValidGpeWindow
    ? `Get Paid Early lets employees bring their pay forward to a day after the pay cycle cut-off, up to and including payday. Based on this calendar's cut-off and payday, you can allow up to ${maxDaysBeforePayday} ${
        maxDaysBeforePayday === 1 ? 'day' : 'days'
      } before payday.`
    : "Get Paid Early lets employees bring their pay forward to a day after the pay cycle cut-off, up to and including payday. This calendar has no days between cut-off and payday, so Get Paid Early can't be enabled.";

  const salariedGpeEnabled = useWatch({ control, name: 'salariedGpeEnabled' });
  const nonSalariedGpeEnabled = useWatch({ control, name: 'nonSalariedGpeEnabled' });
  const enabledBySide: Record<PayCalendarGpeSide['enabledName'], boolean> = {
    salariedGpeEnabled: !!salariedGpeEnabled,
    nonSalariedGpeEnabled: !!nonSalariedGpeEnabled,
  };

  return (
    <VStack align="stretch" spacing="28px" w="full">
      <Flex align="center" gap="6px">
        <Text textStyle="h6SemiBold" color="brand.darkPurple">
          Get Paid Early
        </Text>
        <Tooltip hasArrow shouldWrapChildren arrowPadding={0} placement="top" label={gpeTooltip}>
          <Flex data-testid="gpe-info-tooltip">
            <InfoCircleOutline boxSize="24px" color="brand.darkGray" cursor="pointer" />
          </Flex>
        </Tooltip>
      </Flex>

      {serverError && (
        <Text color="red.500" fontSize="sm" data-testid="gpe-server-error">
          {serverError}
        </Text>
      )}

      {GPE_SIDES.map((side) => {
        const matIsPayday = matIsPaydayBySide[side.enabledName];
        const isEnabled = enabledBySide[side.enabledName];

        if (!matIsPayday) {
          return (
            <VStack key={side.enabledName} align="stretch" spacing="16px">
              <Box data-testid={`${side.enabledName}-mat-hint`}>
                <CustomAlert
                  variant="simple"
                  status="warning"
                  boxShadow="none"
                  title={`Get Paid Early requires the ${side.label.toLowerCase()} Made Available Time to be set to “On Payday”.`}
                />
              </Box>
            </VStack>
          );
        }

        return (
          <VStack key={side.enabledName} align="stretch" spacing="16px">
            <Flex align="center" justify="space-between">
              <Text>{`Enable Get Paid Early — ${side.label}`}</Text>
              <Controller
                control={control}
                name={side.enabledName}
                render={({ field: { onChange, value, ref } }) => (
                  <Switch
                    ref={ref}
                    aria-label={`Enable Get Paid Early — ${side.label}`}
                    isChecked={!!value}
                    isDisabled={!hasValidGpeWindow}
                    onChange={(event) => onChange(event.target.checked)}
                  />
                )}
              />
            </Flex>

            {!hasValidGpeWindow ? (
              <Box data-testid={`${side.enabledName}-no-window-hint`}>
                <CustomAlert
                  variant="simple"
                  status="info"
                  boxShadow="none"
                  title="Get Paid Early can’t be enabled because there are no days between this calendar’s pay cycle cut-off and payday."
                />
              </Box>
            ) : (
              isEnabled && (
                <FormControl isInvalid={!!errors?.[side.capName]?.message}>
                  <FormLabel>Days before payday cap</FormLabel>
                  <Controller
                    control={control}
                    name={side.capName}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <DropdownInput
                        placeholder="Maximum days before payday a request can complete"
                        defaultValue={capOptions.find((option) => option.value === value)}
                        options={capOptions}
                        onChange={onChange}
                        onBlur={onBlur}
                      />
                    )}
                  />
                  <FormHelperText>
                    The most days before payday an employee can bring their pay forward to. They can
                    pick any day after the pay cycle cut-off, up to and including payday — the
                    cut-off day itself is excluded to avoid timing issues.
                  </FormHelperText>
                  <FormErrorMessage>{errors?.[side.capName]?.message}</FormErrorMessage>
                </FormControl>
              )
            )}
          </VStack>
        );
      })}
    </VStack>
  );
}

export default PayCalendarGpeFields;
